import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import clientSocket from '@/services/clientSocket';

// 生成任务状态接口
interface GenerationJob {
  jobId: string;
  imageUrl: string; // 关联的图片URL
  startTime: number;
  status: 'running' | 'completed' | 'failed';
  result?: any;
  error?: any;
}

// 编辑上下文接口
interface EditingContext {
  originalImage: string;
  modifiedImages: string[];
  suggestion: string;
  focus: number;
  selectedThumbnails: Set<number>;
  selectedModifiedImages: Set<number>;
  referencedImages: string[];
  isGenerating: boolean;
  elapsedTime: number;
  generationStartTime?: number;
  lastActiveTime: number; // 上次活跃时间
  currentJobId?: string; // 当前任务ID
}

// 上下文管理器接口
interface EditingContextManager {
  contexts: Map<string, EditingContext>;
  activeContext: string | null;
  createContext: (
    imageUrl: string,
    initialData?: Partial<EditingContext>,
  ) => void;
  switchContext: (imageUrl: string) => EditingContext | null;
  updateContext: (imageUrl: string, updates: Partial<EditingContext>) => void;
  removeContext: (imageUrl: string) => void;
  getContext: (imageUrl: string) => EditingContext | null;
  getGeneratingContexts: () => Array<{
    imageUrl: string;
    context: EditingContext;
  }>;
  hasActiveGenerations: () => boolean;

  // 任务管理方法
  startGeneration: (imageUrl: string, jobId: string) => void;
  getJobStatus: (jobId: string) => GenerationJob | null;

  // 计时器管理方法
  getElapsedTime: (imageUrl: string) => number;

  // 新增：socket结果处理
  getGenerationResult: (
    imageUrl: string,
  ) => { type: 'success' | 'error'; data: any } | null;
  clearGenerationResult: (imageUrl: string) => void;
}

const EditingContextContext = createContext<EditingContextManager | null>(null);

export const useEditingContext = () => {
  const context = useContext(EditingContextContext);
  if (!context) {
    throw new Error(
      'useEditingContext must be used within EditingContextProvider',
    );
  }
  return context;
};

export const EditingContextProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [contexts, setContexts] = useState<Map<string, EditingContext>>(
    new Map(),
  );
  const [activeContext, setActiveContext] = useState<string | null>(null);

  // 新增：任务管理状态
  const [generationJobs, setGenerationJobs] = useState<
    Map<string, GenerationJob>
  >(new Map());
  const [generationResults, setGenerationResults] = useState<
    Map<string, { type: 'success' | 'error'; data: any }>
  >(new Map());

  // 新增：计时器管理
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const [, forceUpdate] = useState({});

  // 强制组件重新渲染的函数
  const triggerUpdate = useCallback(() => {
    forceUpdate({});
  }, []);

  // Socket事件监听管理
  useEffect(() => {
    const handleSuccess = (data: any) => {
      const jobId = data.jobId;
      const resp = data.resp;

      // 直接使用函数式更新，获取当前最新状态
      setGenerationJobs((prevJobs) => {
        const job = prevJobs.get(jobId);
        if (!job) return prevJobs;

        // 更新任务状态
        const newJobs = new Map(prevJobs);
        newJobs.set(jobId, { ...job, status: 'completed', result: data });

        // 同时更新其他相关状态
        setGenerationResults((prev) => {
          const newResults = new Map(prev);
          newResults.set(job.imageUrl, { type: 'success', data });
          return newResults;
        });

        setContexts((prev) => {
          const newContexts = new Map(prev);
          const context = newContexts.get(job.imageUrl);
          if (context && resp && Array.isArray(resp)) {
            const newModifiedImages = [...context.modifiedImages, ...resp];
            const newFocus = context.modifiedImages.length;

            newContexts.set(job.imageUrl, {
              ...context,
              modifiedImages: newModifiedImages,
              focus: newFocus,
              isGenerating: false,
              currentJobId: undefined,
            });
          } else if (context) {
            newContexts.set(job.imageUrl, {
              ...context,
              isGenerating: false,
              currentJobId: undefined,
            });
          }
          return newContexts;
        });

        return newJobs;
      });
    };

    const handleError = (data: any) => {
      const jobId = data.jobId;

      setGenerationJobs((prevJobs) => {
        const job = prevJobs.get(jobId);
        if (!job) return prevJobs;

        const newJobs = new Map(prevJobs);
        newJobs.set(jobId, { ...job, status: 'failed', error: data });

        setGenerationResults((prev) => {
          const newResults = new Map(prev);
          newResults.set(job.imageUrl, { type: 'error', data });
          return newResults;
        });

        setContexts((prev) => {
          const newContexts = new Map(prev);
          const context = newContexts.get(job.imageUrl);
          if (context) {
            newContexts.set(job.imageUrl, {
              ...context,
              isGenerating: false,
              currentJobId: undefined,
            });
          }
          return newContexts;
        });

        return newJobs;
      });
    };

    // 注册socket监听器
    if (clientSocket.socket) {
      clientSocket.socket.on('job:done', handleSuccess);
      clientSocket.socket.on('job:failed', handleError);
    }

    return () => {
      // 清理socket监听器
      if (clientSocket.socket) {
        clientSocket.socket.off('job:done', handleSuccess);
        clientSocket.socket.off('job:failed', handleError);
      }
    };
  }, []);

  // 计时器管理
  useEffect(() => {
    // 启动定时器，每秒更新一次所有正在生成的任务的elapsed time
    const startTimer = () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }

      timerRef.current = setInterval(() => {
        const now = Date.now();
        let hasGenerating = false;

        setContexts((prev) => {
          const newContexts = new Map(prev);
          let hasUpdates = false;

          for (const [imageUrl, context] of newContexts) {
            if (context.isGenerating && context.generationStartTime) {
              hasGenerating = true;
              const newElapsedTime = Math.floor(
                (now - context.generationStartTime) / 1000,
              );
              if (newElapsedTime !== context.elapsedTime) {
                newContexts.set(imageUrl, {
                  ...context,
                  elapsedTime: newElapsedTime,
                });
                hasUpdates = true;
              }
            }
          }

          return hasUpdates ? newContexts : prev;
        });

        // 如果没有正在生成的任务，停止计时器
        if (!hasGenerating) {
          if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
          }
        }

        // 触发重新渲染
        triggerUpdate();
      }, 1000);
    };

    // 检查是否有正在生成的任务
    const hasGenerating = Array.from(contexts.values()).some(
      (context) => context.isGenerating,
    );

    if (hasGenerating && !timerRef.current) {
      startTimer();
    } else if (!hasGenerating && timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [contexts, triggerUpdate]);

  // 创建新的编辑上下文
  const createContext = useCallback(
    (imageUrl: string, initialData?: Partial<EditingContext>) => {
      const defaultContext: EditingContext = {
        originalImage: imageUrl,
        modifiedImages: [imageUrl],
        suggestion: '',
        focus: 0,
        selectedThumbnails: new Set(),
        selectedModifiedImages: new Set(),
        referencedImages: [],
        isGenerating: false,
        elapsedTime: 0,
        lastActiveTime: Date.now(),
        ...initialData,
      };

      setContexts((prev) => {
        const newContexts = new Map(prev);
        newContexts.set(imageUrl, defaultContext);
        return newContexts;
      });

      setActiveContext(imageUrl);
    },
    [],
  );

  // 切换到指定上下文
  const switchContext = useCallback(
    (imageUrl: string): EditingContext | null => {
      const context = contexts.get(imageUrl);
      if (context) {
        // 检查是否需要更新活跃时间（避免频繁无意义的更新）
        const now = Date.now();
        const timeDiff = now - context.lastActiveTime;

        // 只有时间差超过1秒才更新，避免频繁更新
        if (timeDiff > 1000) {
          setContexts((prev) => {
            const newContexts = new Map(prev);
            const existingContext = newContexts.get(imageUrl);
            if (existingContext) {
              newContexts.set(imageUrl, {
                ...existingContext,
                lastActiveTime: now,
              });
            }
            return newContexts;
          });
        }

        if (activeContext !== imageUrl) {
          setActiveContext(imageUrl);
        }

        return context;
      }
      return null;
    },
    [contexts, activeContext],
  );

  // 更新上下文
  const updateContext = useCallback(
    (imageUrl: string, updates: Partial<EditingContext>) => {
      setContexts((prev) => {
        const newContexts = new Map(prev);
        const existingContext = newContexts.get(imageUrl);
        if (existingContext) {
          const updated = { ...existingContext, ...updates };

          // 检查是否真的有重要变化（忽略 elapsedTime 的单独更新）
          const hasImportantChanges = Object.keys(updates).some((key) => {
            // 如果只更新 elapsedTime，不认为是重要变化
            if (key === 'elapsedTime' && Object.keys(updates).length === 1) {
              return false;
            }

            const oldValue = existingContext[key as keyof EditingContext];
            const newValue = updates[key as keyof EditingContext];

            // 对于 Set 类型的特殊处理
            if (oldValue instanceof Set && newValue instanceof Set) {
              return (
                oldValue.size !== newValue.size ||
                [...oldValue].some((item) => !newValue.has(item))
              );
            }

            // 对于数组类型的特殊处理
            if (Array.isArray(oldValue) && Array.isArray(newValue)) {
              return (
                oldValue.length !== newValue.length ||
                oldValue.some((item, index) => item !== newValue[index])
              );
            }

            return oldValue !== newValue;
          });

          if (hasImportantChanges) {
            newContexts.set(imageUrl, updated);
          } else {
            // 即使没有重要变化，也要更新 elapsedTime（但不打印日志）
            if (updates.elapsedTime !== undefined) {
              newContexts.set(imageUrl, updated);
            } else {
              // 完全没有变化，不更新
              return prev;
            }
          }
        } else {
          // 尝试更新不存在的上下文，不做处理
        }
        return newContexts;
      });
    },
    [],
  );

  // 移除上下文
  const removeContext = useCallback(
    (imageUrl: string) => {
      setContexts((prev) => {
        const newContexts = new Map(prev);
        newContexts.delete(imageUrl);
        return newContexts;
      });

      if (activeContext === imageUrl) {
        setActiveContext(null);
      }
    },
    [activeContext],
  );

  // 获取上下文
  const getContext = useCallback(
    (imageUrl: string): EditingContext | null => {
      return contexts.get(imageUrl) || null;
    },
    [contexts],
  );

  // 获取所有正在生成的上下文
  const getGeneratingContexts = useCallback(() => {
    const generating: Array<{ imageUrl: string; context: EditingContext }> = [];
    for (const [imageUrl, context] of contexts) {
      if (context.isGenerating) {
        generating.push({ imageUrl, context });
      }
    }
    return generating;
  }, [contexts]);

  // 检查是否有活跃的生成任务
  const hasActiveGenerations = useCallback(() => {
    for (const context of contexts.values()) {
      if (context.isGenerating) {
        return true;
      }
    }
    return false;
  }, [contexts]);

  // 新增：开始生成任务
  const startGeneration = useCallback((imageUrl: string, jobId: string) => {
    const now = Date.now();

    // 创建新任务
    setGenerationJobs((prev) => {
      const newJobs = new Map(prev);
      newJobs.set(jobId, {
        jobId,
        imageUrl,
        startTime: now,
        status: 'running',
      });
      return newJobs;
    });

    // 更新上下文
    setContexts((prev) => {
      const newContexts = new Map(prev);
      const context = newContexts.get(imageUrl);
      if (context) {
        newContexts.set(imageUrl, {
          ...context,
          isGenerating: true,
          currentJobId: jobId,
          generationStartTime: now,
          elapsedTime: 0,
        });
      }
      return newContexts;
    });

    // 订阅socket事件
    if (clientSocket.socket && clientSocket.socket.connected) {
      clientSocket.socket.emit('subscribe', jobId);
    }
  }, []);

  // 获取任务状态
  const getJobStatus = useCallback(
    (jobId: string): GenerationJob | null => {
      return generationJobs.get(jobId) || null;
    },
    [generationJobs],
  );

  // 获取经过时间
  const getElapsedTime = useCallback(
    (imageUrl: string): number => {
      const context = contexts.get(imageUrl);
      if (!context) return 0;

      if (context.isGenerating && context.generationStartTime) {
        return Math.floor((Date.now() - context.generationStartTime) / 1000);
      }

      return context.elapsedTime || 0;
    },
    [contexts],
  );

  // 获取生成结果
  const getGenerationResult = useCallback(
    (imageUrl: string) => {
      return generationResults.get(imageUrl) || null;
    },
    [generationResults],
  );

  // 清除生成结果
  const clearGenerationResult = useCallback((imageUrl: string) => {
    setGenerationResults((prev) => {
      const newResults = new Map(prev);
      newResults.delete(imageUrl);
      return newResults;
    });
  }, []);

  const value: EditingContextManager = {
    contexts,
    activeContext,
    createContext,
    switchContext,
    updateContext,
    removeContext,
    getContext,
    getGeneratingContexts,
    hasActiveGenerations,
    startGeneration,
    getJobStatus,
    getElapsedTime,
    getGenerationResult,
    clearGenerationResult,
  };

  return (
    <EditingContextContext.Provider value={value}>
      {children}
    </EditingContextContext.Provider>
  );
};
